/**
 * AdminFounderDocs — the founder's private download center.
 *
 * Lists the investor-facing documents (pitch deck, recording script,
 * angel outreach kit, diligence FAQ, etc.) committed to /app/memory/
 * with last-updated timestamps + one-click download. Admin-gated by
 * the backend.
 *
 * Why this exists: production users (including the founder herself)
 * don't have ssh access to the deployed pod. This UI is the bridge
 * from the live trubeaute.com session to the files in the repo.
 */
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Download, FileText, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const AdminFounderDocs = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/founder-docs`, {
        withCredentials: true,
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(
        e?.response?.status === 403
          ? "Founder-only — sign in as an admin."
          : "Couldn't load founder docs.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDownload = async (item) => {
    setDownloading(item.filename);
    try {
      const res = await axios.get(
        `${API}/admin/founder-docs/${item.filename}`,
        { withCredentials: true, responseType: "blob" },
      );
      // Trigger an inline download in the browser without leaving the page
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke the URL on the next tick — Safari needs the link alive
      // briefly to actually trigger the save dialog.
      setTimeout(() => URL.revokeObjectURL(url), 500);
      toast.success(`${item.filename} downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0F0D14] text-white pb-24"
      data-testid="admin-founder-docs-page"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-8">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1.5 text-white/55 hover:text-white text-sm transition mb-6"
          data-testid="founder-docs-back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </button>

        <motion.header
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8"
        >
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4B483] mb-2">
            Founder · investor materials
          </p>
          <h1 className="text-3xl sm:text-4xl font-medium text-white">
            Founder Docs
          </h1>
          <p className="text-white/55 text-sm mt-2 max-w-2xl">
            The current versions of your pitch deck, voice recording script,
            angel outreach kit, and diligence materials. Sourced live from
            the repo — what you download here is exactly what the engineering
            team is shipping.
          </p>
        </motion.header>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-white/55 py-12">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading docs…
          </div>
        ) : items.length === 0 ? (
          <div className="text-white/55 text-sm py-12 text-center">
            No founder docs available on this server.
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((item) => {
              const Icon = FileText;
              const isDownloading = downloading === item.filename;
              return (
                <motion.li
                  key={item.filename}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-[#1A1720] border border-white/8 rounded-2xl p-4 sm:p-5 flex items-start justify-between gap-3 hover:border-[#D4B483]/40 transition"
                  data-testid={`founder-doc-${item.filename}`}
                >
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="shrink-0 p-2 rounded-lg bg-[#D4B483]/15 text-[#D4B483]">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-white font-medium text-base truncate">
                        {item.label}
                      </h2>
                      <p className="text-[11px] text-white/40 font-mono mt-0.5 truncate">
                        {item.filename}
                      </p>
                      <p className="text-[11px] text-white/50 mt-1">
                        Updated {formatDate(item.updated_at)}
                        {item.source_filename && (
                          <span className="text-white/35"> · rendered from {item.source_filename}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownload(item)}
                    disabled={isDownloading}
                    data-testid={`founder-doc-download-${item.filename}`}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-[#D4B483]/15 hover:text-[#D4B483] text-white/70 rounded-full text-xs font-medium transition disabled:opacity-40"
                  >
                    {isDownloading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    {isDownloading ? "Saving…" : "Download PDF"}
                  </button>
                </motion.li>
              );
            })}
          </ul>
        )}

        <div className="mt-10 p-4 bg-white/5 border border-white/10 rounded-xl text-xs text-white/55 leading-relaxed">
          <strong className="text-white/75">How this works:</strong> Engineering
          edits the source markdown / CSV in <code className="bg-white/10 mx-1 px-1 rounded text-[10px]">/app/memory/</code>
          whenever a feature ships. Every download here is rendered on-demand
          to a brand-styled <code className="bg-white/10 mx-1 px-1 rounded text-[10px]">.pdf</code>
          (using the TruBeauté palette, Playfair serif headings, page numbers)
          — so what you send to an investor always reflects what's currently
          true. The timestamps tell you what was modified last.
        </div>
      </div>
    </div>
  );
};

export default AdminFounderDocs;
